import api from './api';
import { createUuid } from '@/utils/uuid';
import type { ActionLogListResponse } from './action-log.service';
import type { CreateCostingRunPayload } from '@/pages/Inventory/costing-runs/types';
import {
  ReceiptType,
  SerialStatus,
  StockTakeScope,
  StockTakeStatus,
  TrackingMode,
} from '@tcr-erp/shared';

export { ReceiptType, SerialStatus, StockTakeScope, StockTakeStatus, TrackingMode };

export interface CostingRunPreviewResponse {
  issue_count: number;
  dirty_documents: Array<{
    id: string;
    type: string;
    date: string;
  }>;
}

export interface CostingRunProgress {
  total_steps: number;
  completed_steps: number;
  current_step: string;
}

export interface CostingRun {
  _id: string;
  run_number: string;
  store_id: string;
  method: string;
  period_start: string;
  period_end: string;
  status: string;
  progress: CostingRunProgress;
  processed_issues: number;
  total_issues: number;
  total_exceptions?: number;
  exceptions?: CostingRunException[];
}

export interface CostingRunDetail {
  id: string;
  run_number: string;
  scope_key: string;
  method: string;
  store_id?: string;
  period_start: string;
  period_end: string;
  period_granularity: string;
  product_scope: string;
  product_ids: string[];
  calculate_by_warehouse: boolean;
  branch_costing_scope: string;
  warn_on_negative_stock: boolean;
  status: string;
  progress?: {
    total_steps: number;
    completed_steps: number;
    current_step?: string;
  };
  total_issues?: number;
  processed_issues?: number;
  total_exceptions?: number;
  exceptions?: CostingRunException[];
}

export interface CostingRunException {
  issue_document_id: string;
  issue_line_id?: string;
  product_id?: string;
  code: string;
  message: string;
}

export interface CostingRunLog {
  _id: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  event: string;
  message: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface CostingRunLogPage {
  data: CostingRunLog[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface CostingRunLogsQuery {
  limit?: number;
  before?: string;
}

export interface CostingRunCreateData {
  _id: string;
}

export function isCostingRunCreateData(value: unknown): value is CostingRunCreateData {
  if (typeof value !== 'object' || value === null) return false;

  const data = value as Record<string, unknown>;
  return typeof data._id === 'string' && data._id.length > 0;
}

/** Query cho `GET /inventory/movements` — match BE
 *  [MovementListQueryDto](erpgold-be-tenant/src/modules/inventory/dto/movement-list-query.dto.ts).
 *  BE trả `TenantStockMovementDto` đã map type 10→4 cho FE consume trực tiếp. */
export interface MovementsListQuery {
  from?: string;
  to?: string;
  type?: string;
  store_id?: string;
  warehouse_id?: string;
  product_id?: string;
  variant_id?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

/** Response shape cho movements list. */
export interface MovementsListResponse {
  data: ({
    _id: string;
    product_id: string;
    variant_id?: string;
    sku: string;
    product_name: string;
    type: string;
    quantity: number;
    balance_after?: number;
    reference_type: string;
    reference_id: string;
    reference_code?: string;
    staff_id?: string;
    reason?: string;
    note?: string;
    created_at: string;
    raw_type?: string;
  })[];
  total: number;
}

// ─── Inventory list ────────────────────────────────────────────────────────

export interface InventoryListResponse {
  data: {
    _id: string;
    product_id: string;
    variant_id: string;
    sku: string;
    product_name: string;
    category_name?: string;
    warehouse_id: string;
    store_id?: string;
    unit_in_stock?: number;
    quantity: number;
    available_quantity: number;
    reserved_quantity: number;
    tracking_mode: string;
    unit_cost?: number;
    updated_at: string;
  }[];
  total: number;
}

export interface InventoryScopeSummary {
  summary: {
    total_sku: number;
    total_quantity: number;
    total_reserved: number;
    total_available: number;
    low_stock_count: number;
    out_of_stock_count: number;
  };
  stores: Array<{
    store_id: string;
    store_name: string;
    store_code: string;
    total_sku: number;
    total_quantity: number;
    total_reserved: number;
    total_available: number;
    warehouse_count: number;
    low_stock_count: number;
    out_of_stock_count: number;
  }>;
  warehouses: Array<{
    warehouse_id: string;
    warehouse_name: string;
    warehouse_code: string;
    store_id: string;
    store_name: string;
    total_sku: number;
    total_quantity: number;
    total_reserved: number;
    total_available: number;
    low_stock_count: number;
    out_of_stock_count: number;
  }>;
}


// ─── Quick mutations (manual stock-in/out/adjust/transfer) ────────────────
// ADR-013: API JSON snake_case end-to-end. BE DTO `StockInDto/StockOutDto/
// StockAdjustDto` (xem erpgold-be-tenant inventory dto/stock-mutation.dto.ts).

export interface StockMovementDto {
  _id: string;
  tenant_id: string;
  store_id: string;
  warehouse_id: string;
  type: string;
  product_id: string;
  variant_id?: string;
  quantity: number;
  balance_after?: number;
  reference_type: string;
  reference_id: string;
  reference_code?: string;
  staff_id?: string;
  reason?: string;
  note?: string;
  created_at: string;
}

// ─── Goods Receipts (phiếu nhập) ──────────────────────────────────────────

export interface GoodsReceiptItemPayload {
  warehouse_id?: string;
  product_id: string;
  variant_id: string;
  product_code?: string;
  product_name?: string;
  variant_code?: string;
  tracking_mode?: 'quantity' | 'serial';
  quantity: number;
  unit_price?: number;
  uom_id?: string;
  uom_code?: string;
  conversion_rate?: number;
  document_quantity?: number;
  debit_account_code?: string;
  credit_account_code?: string;
  serials?: string[];
  lot_allocations?: Array<{
    inventory_location_balance_id?: string;
    lot_id?: string | null;
    lot_number?: string;
    warehouse_location_id?: string | null;
    warehouse_location_code?: string;
    expiry_date?: string | null;
    quantity: number;
    override_reason?: string;
  }>;
  note?: string;
  lot_number?: string;
  supplier_lot_number?: string;
  expiry_date?: string;
  warehouse_location_id?: string;
  warehouse_location_code?: string;
}

export interface ReceiveGoodsReceiptDraftItemPayload {
  group_name: string;
  item_name: string;
  gold_type?: string;
  total_weight: number;
  stone_weight: number;
  gold_weight: number;
  sell_labor: number;
  import_labor: number;
  quantity: number;
  note?: string;
}

export interface CreateReceiveGoodsReceiptPayload {
  receipt_type?: 'purchase' | 'production' | 'transfer' | 'return' | 'adjustment' | 'other';
  warehouse_id: string;
  store_id?: string;
  supplier_id?: string;
  note?: string;
  items: ReceiveGoodsReceiptDraftItemPayload[];
}

export interface CreateGoodsReceiptPayload {
  receipt_type: 'purchase' | 'production' | 'transfer' | 'return' | 'adjustment' | 'other';
  warehouse_id?: string;
  store_id?: string;
  supplier_id?: string;
  purchase_order_id?: string;
  source_code?: string;
  document_date?: string;
  accounting_date?: string;
  document_no?: string;
  accounting_owner?: string;
  supplier_code?: string;
  supplier_name?: string;
  supplier_address?: string;
  delivery_person?: string;
  note?: string;
  items: GoodsReceiptItemPayload[];
}

// ─── Goods Issues (phiếu xuất) ────────────────────────────────────────────

export interface GoodsIssueItemPayload {
  product_id: string;
  variant_id: string;
  warehouse_id: string;
  tracking_mode?: 'quantity' | 'serial';
  quantity: number;
  unit_cost?: number;
  uom_id?: string;
  uom_code?: string;
  conversion_rate?: number;
  document_quantity?: number;
  debit_account_code?: string;
  credit_account_code?: string;
  serials?: string[];
  note?: string;
  source_line_id?: string;
  sales_invoice_id?: string;
  sales_invoice_number?: string;
  sold_quantity?: number;
  issued_before_quantity?: number;
}

export interface CreateGoodsIssuePayload {
  purpose: 'sales_issue' | 'internal_use' | 'workshop' | 'scrap' | 'expired' | 'damaged' | 'adjustment' | 'transfer_out' | 'promotion' | 'display' | 'sample' | 'return_to_supplier' | 'other';
  warehouse_id?: string;
  store_id?: string;
  document_date?: string;
  accounting_date?: string;
  document_no?: string;
  accounting_owner?: string;
  customer_id?: string;
  customer_code?: string;
  customer_name?: string;
  customer_address?: string;
  receiver_name?: string;
  salesperson?: string;
  delivery_address?: string;
  source_type?: string;
  source_id?: string;
  source_code?: string;
  note?: string;
  items: GoodsIssueItemPayload[];
}

export interface SalesIssueInvoiceLine {
  line_id: string;
  product_id: string;
  variant_id: string;
  warehouse_id: string;
  product_code?: string;
  product_name: string;
  variant_code?: string;
  warehouse_code?: string;
  warehouse_name?: string;
  uom_code?: string;
  tracking_mode?: 'quantity' | 'serial';
  serials?: string[];
  sold_quantity: number;
  issued_quantity: number;
  remaining_quantity: number;
  cost_price?: number;
  debit_account_code?: string;
  credit_account_code?: string;
}

export interface SalesIssueInvoiceOption {
  _id: string;
  invoice_number: string;
  invoice_date?: string;
  customer_id?: string;
  customer_code?: string;
  customer_name?: string;
  customer_address?: string;
  salesperson?: string;
  store_id?: string;
  status?: string;
  sold_quantity: number;
  issued_quantity: number;
  remaining_quantity: number;
  lines: SalesIssueInvoiceLine[];
}

export interface SalesIssueInvoiceQuery {
  store_id?: string;
  customer_id?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  page_size?: number;
}

// ─── Stock Transfers (phiếu chuyển) ───────────────────────────────────────

export interface StockTransferItemPayload {
  product_id: string;
  variant_id: string;
  from_warehouse_id?: string;
  to_warehouse_id?: string;
  tracking_mode?: 'quantity' | 'serial';
  quantity: number;
  uom_id?: string;
  uom_code?: string;
  conversion_rate?: number;
  document_quantity?: number;
  debit_account_code?: string;
  credit_account_code?: string;
  unit_price?: number;
  unit_cost?: number;
  serials?: string[];
  lot_allocations?: Array<{
    inventory_location_balance_id?: string;
    lot_id?: string | null;
    lot_number?: string;
    warehouse_location_id?: string | null;
    warehouse_location_code?: string;
    expiry_date?: string | null;
    quantity: number;
    override_reason?: string;
  }>;
  lot_number?: string;
  supplier_lot_number?: string;
  expiry_date?: string;
  warehouse_location_id?: string;
  warehouse_location_code?: string;
  note?: string;
}

export interface CreateStockTransferPayload {
  transfer_business_type?: 'warehouse_transfer' | 'inter_branch_transfer' | 'dependent_branch_issue';
  from_store_id: string;
  to_store_id: string;
  from_warehouse_id?: string;
  to_warehouse_id?: string;
  document_date?: string;
  accounting_date?: string;
  document_no?: string;
  carrier_name?: string;
  note?: string;
  /** TCR-2665 — lý do lấy vào hàng đặt giữ (bắt buộc khi có dòng vượt số bán được). */
  reserved_override_reason?: string;
  items: StockTransferItemPayload[];
}

export interface StockTransferReferenceResponse {
  _id?: string;
  stock_transfer_id?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readTransferIdentifier(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value;
  if (isRecord(value) && typeof value.$oid === 'string' && value.$oid.trim()) return value.$oid;
  return undefined;
}

function normalizeStockTransferReference(value: unknown): StockTransferReferenceResponse {
  if (!isRecord(value)) return {};

  const reference: StockTransferReferenceResponse = {};
  const id = readTransferIdentifier(value._id);
  const stockTransferId = readTransferIdentifier(value.stock_transfer_id);
  if (id) reference._id = id;
  if (stockTransferId) reference.stock_transfer_id = stockTransferId;
  return reference;
}

// ─── Stock Takes (kiểm kê) ────────────────────────────────────────────────

export interface CreateStockTakePayload {
  store_id: string;
  warehouse_id: string;
  name?: string;
  counted_at?: string;
  timezone?: string;
  chairperson?: string;
  warehouse_representative?: string;
  accounting_representative?: string;
  members?: string[];
  /**
   * Cách user chọn scope. BE resolve scope + scope_value → product_ids:
   * - full: count toàn warehouse (scope_value bỏ qua)
   * - category: scope_value = [category_id...]
   * - karat: scope_value = ['24K','18K',...]
   * - custom: scope_value = [variant_id...] (legacy vẫn chấp nhận product_id)
   */
  scope?: StockTakeScope;
  scope_value?: string[];
  /** Legacy — chỉ dùng khi caller không gửi scope, BE treat as CUSTOM. */
  product_ids?: string[];
  note?: string;
  count_mode?: 'scheduled' | 'ad_hoc';
  location_ids?: string[];
}

export interface StockTakeCountItemPayload {
  item_id: string;
  variant_id?: string;
  actual_quantity: number;
  actual_serials?: string[];
  diff_reason?: string;
  resolution?: 'none' | 'receipt_surplus' | 'issue_shortage';
  resolution_account_code?: string;
}

// ─── List query + update payloads ──────────────────────────────────────────

export interface GoodsReceiptListQuery {
  sync_filter?: 'ALL' | 'NOT_SYNCED' | 'IN_PROGRESS' | 'DONE' | 'ERROR';
  receipt_type?: ReceiptType;
  status?: string;
  warehouse_id?: string;
  store_id?: string;
  partner_search?: string;
  staff_search?: string;
  search?: string;
  include_system_generated?: boolean;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface UpdateGoodsReceiptPayload {
  receipt_type?: 'purchase' | 'production' | 'transfer' | 'return' | 'adjustment' | 'other';
  warehouse_id?: string;
  store_id?: string;
  document_date?: string;
  accounting_date?: string;
  document_no?: string;
  supplier_id?: string;
  supplier_code?: string;
  supplier_name?: string;
  supplier_address?: string;
  delivery_person?: string;
  note?: string;
  items?: GoodsReceiptItemPayload[];
}

export interface GoodsIssueListQuery {
  sync_filter?: 'ALL' | 'NOT_SYNCED' | 'IN_PROGRESS' | 'DONE' | 'ERROR';
  purpose?: string;
  status?: string;
  warehouse_id?: string;
  store_id?: string;
  partner_search?: string;
  staff_search?: string;
  search?: string;
  include_system_generated?: boolean;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface UpdateGoodsIssuePayload {
  purpose?: 'sales_issue' | 'internal_use' | 'workshop' | 'scrap' | 'expired' | 'damaged' | 'adjustment' | 'transfer_out' | 'promotion' | 'display' | 'sample' | 'return_to_supplier' | 'other';
  warehouse_id?: string;
  store_id?: string;
  document_date?: string;
  accounting_date?: string;
  document_no?: string;
  customer_id?: string;
  customer_code?: string;
  customer_name?: string;
  customer_address?: string;
  receiver_name?: string;
  salesperson?: string;
  delivery_address?: string;
  source_type?: string;
  source_id?: string;
  source_code?: string;
  note?: string;
  items?: GoodsIssueItemPayload[];
}

export interface StockTransferListQuery {
  sync_filter?: 'ALL' | 'NOT_SYNCED' | 'IN_PROGRESS' | 'DONE' | 'ERROR';
  status?: string;
  transfer_business_type?: 'warehouse_transfer' | 'inter_branch_transfer' | 'dependent_branch_issue';
  store_id?: string;
  from_store_id?: string;
  to_store_id?: string;
  from_warehouse_id?: string;
  to_warehouse_id?: string;
  staff_search?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  created_from?: string;
  created_to?: string;
  page?: number;
  page_size?: number;
}

export interface UpdateStockTransferPayload {
  transfer_business_type?: 'warehouse_transfer' | 'inter_branch_transfer' | 'dependent_branch_issue';
  from_store_id?: string;
  to_store_id?: string;
  from_warehouse_id?: string;
  to_warehouse_id?: string;
  document_date?: string;
  accounting_date?: string;
  document_no?: string;
  carrier_name?: string;
  note?: string;
  /** TCR-2665 — lý do lấy vào hàng đặt giữ (bắt buộc khi có dòng vượt số bán được). */
  reserved_override_reason?: string;
  items?: StockTransferItemPayload[];
}

export interface StockTakeListQuery {
  status?: string;
  store_id?: string;
  warehouse_id?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

// ─── List query types ─────────────────────────────────────────────────────

export interface ListPaginatedQuery {
  page?: number;
  page_size?: number;
  status?: string;
  search?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: { total: number };
}

export interface CostExceptionQuery {
  status?: string;
  sku?: string;
  search?: string;
  warehouse_id?: string;
  accounting_period?: string;
  age_days?: number;
  page?: number;
  limit?: number;
}

export interface CostExceptionRow {
  exception_type:
    | 'unvalued_receipt'
    | 'zero_cost_warning'
    | 'goods_issue_costing'
    | 'packed_accounting'
    | 'invoice_without_goods_issue';
  document_type: 'stock_movement' | 'goods_issue' | 'packed_order';
  document_id: string;
  document_code: string;
  status: string;
  reason_code: string;
  sku: string | null;
  product_id?: string | null;
  variant_id: string | null;
  warehouse_id: string | null;
  warehouse_name?: string | null;
  quantity: number | null;
  value: number | null;
  accounting_date: string | null;
  source_document_type?: string | null;
  source_document_id?: string | null;
  goods_issue_id?: string | null;
  order_id?: string;
  sales_invoice_id?: string | null;
  scenario_execution_id?: string | null;
  issue_movement_ids?: string[];
  source_movement_ids?: string[];
  pending_source_movement_ids?: string[];
}

export interface CostExceptionPage {
  success: true;
  data: CostExceptionRow[];
  total: number;
  page: number;
  limit: number;
}

export interface ZeroCostApproval {
  movement_id: string;
  approved_by: string;
  approved_at: string;
  evidence_ref: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  page_size: number;
}

// ─── Shared types ────────────────────────────────────────────────────────────

interface StockMovementValue {
  quantity: number;
  value: number;
}

interface InventoryByProduct {
  product_id: string;
  product_name: string;
  sku: string;
  variant_id?: string;
  unit?: string;
  unit_name?: string;
  uom?: string;
  unit_code?: string;
  store_id?: string;
  store_code?: string;
  store_name?: string;
  warehouse_id?: string;
  warehouse_code?: string;
  warehouse_name?: string;
  category: string;
  opening_quantity: number;
  opening_value?: number;
  stock_in: StockMovementValue;
  stock_out: StockMovementValue;
  closing_quantity: number;
  closing_value: number;
}

interface InventorySummaryData {
  period: { from: string; to: string };
  summary: {
    opening_quantity: number;
    opening_value?: number;
    stock_in: StockMovementValue;
    stock_out: StockMovementValue;
    closing_quantity: number;
    closing_value: number;
  };
  by_product: InventoryByProduct[];
  pagination?: { page: number; page_size: number; total: number; total_pages: number };
}

export interface WarehouseInfo {
  _id: string;
  code: string;
  name: string;
  name_alias?: string;
  store_id?: string;
  store_name?: string;
  state?: number;
  is_main_warehouse?: boolean;
  group_name?: string;
  targets?: string[];
  tracking_type?: string;
  auto_adjust?: boolean;
  allow_negative?: boolean;
  allow_negative_stock?: boolean;
  lot_tracking?: boolean;
  location_tracking?: boolean;
  stock_alert_enabled?: boolean;
  require_approval_for_adjustment?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface StockSerialItem {
  _id: string;
  serial_number: string;
  variant_id: string;
  product_id: string;
  warehouse_id: string;
  store_id?: string;
  status: SerialStatus | null;
  purchase_price?: number;
  sold_at?: string;
  order_id?: string;
  warranty_expires_at?: string;
  created_at: string;
  updated_at: string;
  variant_code?: string;
  product_name?: string;
  product_sku?: string;
  store_code?: string;
  store_name?: string;
  warehouse_code?: string;
  warehouse_name?: string;
}

export interface SerialQueryParams {
  store_id?: string;
  warehouse_id?: string;
  variant_id?: string;
  product_id?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// ─── Inventory list query ─────────────────────────────────────────────────────

export interface InventoryListQuery {
  search?: string;
  store_id?: string;
  warehouse_id?: string;
  group_by?: 'variant_warehouse' | 'variant_store' | 'variant_total';
  category?: string;
  category_id?: string;
  karat?: string;
  low_stock?: boolean;
  page?: number;
  page_size?: number;
}

export interface LowStockAlertsQuery {
  /** Tìm theo mã SKU/mã biến thể hoặc tên sản phẩm/biến thể. */
  search?: string;
  store_id?: string;
  warehouse_id?: string;
  category_id?: string;
}

export interface LowStockAlert {
  product_id: string;
  variant_id: string;
  sku: string;
  variant_code?: string;
  variant_label?: string;
  product_name: string;
  warehouse_id: string;
  available_quantity: number;
  reorder_point?: number;
}

// ─── Print labels ─────────────────────────────────────────────────────────────

export interface PrintLabelsPayload {
  variant_ids: string[];
  template_id?: string;
  copies?: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const inventoryService = {
  costExceptions: {
    list(params: CostExceptionQuery = {}): Promise<CostExceptionPage> {
      return api.get<CostExceptionPage>('/inventory/cost-exceptions', { params }).then((r) => r.data);
    },
    approveZero(movementId: string, evidenceRef: string): Promise<ApiResponse<ZeroCostApproval>> {
      return api
        .post<ApiResponse<ZeroCostApproval>>(
          `/inventory/cost-exceptions/source-movements/${movementId}/approve-zero`,
          { evidence_ref: evidenceRef },
        )
        .then((r) => r.data);
    },
  },

  // ─── Inventory summary report ────────────────────────────────────────────
  getSummary(params: {
    warehouse_id?: string;
    from: string;
    to: string;
    store_id?: string;
    category_id?: string;
    product_id?: string;
    search?: string;
    column_filters?: string;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<InventorySummaryData>> {
    return api
      .get('/inventory/reports/summary', { params })
      .then((r) => r.data);
  },

  // ─── Inventory list ─────────────────────────────────────────────────────
  list(params: InventoryListQuery): Promise<InventoryListResponse> {
    return api.get('/inventory', { params }).then((r) => r.data);
  },

  tree(params: InventoryListQuery): Promise<InventoryListResponse> {
    return api.get('/inventory/tree', { params }).then((r) => r.data);
  },

  scopeSummary(params: { store_id?: string; warehouse_id?: string }): Promise<InventoryScopeSummary> {
    return api.get('/inventory/scope-summary', { params }).then((r) => r.data);
  },

  movements(params: MovementsListQuery): Promise<PaginatedResponse<unknown>> {
    return api.get('/inventory/movements', { params }).then((r) => r.data);
  },

  lowStock(params: LowStockAlertsQuery = {}): Promise<ApiResponse<LowStockAlert[]>> {
    return api.get('/inventory/alerts', { params }).then((r) => r.data);
  },

  alerts(params: LowStockAlertsQuery = {}): Promise<ApiResponse<LowStockAlert[]>> {
    return api.get('/inventory/alerts', { params }).then((r) => r.data);
  },

  warehouseLocations: {
    list(params?: {
      store_id?: string;
      warehouse_id?: string;
      search?: string;
      aisle?: string;
      status?: string;
      page?: number;
      page_size?: number;
    }): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/warehouse-locations', { params }).then((r) => r.data);
    },
    create(body: {
      warehouse_id?: string;
      warehouse_ids?: string[];
      store_id?: string;
      code: string;
      status?: string;
      location_type?: string;
      note?: string;
    }): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/warehouse-locations', body).then((r) => r.data);
    },
    update(id: string, body: {
      warehouse_ids?: string[];
      code?: string;
      status?: string;
      location_type?: string;
      note?: string;
    }): Promise<ApiResponse<unknown>> {
      return api.patch(`/inventory/warehouse-locations/${id}`, body).then((r) => r.data);
    },
    bulkStatus(body: {
      warehouse_id: string;
      status: string;
      aisle?: string;
      bay?: string;
      level?: string;
    }): Promise<ApiResponse<unknown>> {
      return api.patch('/inventory/warehouse-locations/bulk-status', body).then((r) => r.data);
    },
    remove(id: string): Promise<ApiResponse<unknown>> {
      return api.delete(`/inventory/warehouse-locations/${id}`).then((r) => r.data);
    },
    import(body: {
      warehouse_id: string;
      store_id?: string;
      commit?: boolean;
      rows: Array<{ code: string; status?: string; note?: string }>;
    }): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/warehouse-locations/import', body).then((r) => r.data);
    },
    /**
     * TCR-1973 — nút "Xuất Excel" ở tab "Danh sách" màn "Vị trí kho".
     *
     * Cùng root cause/fix với `warehouseService.exportList()` (module Kho, xem comment đầy đủ ở
     * đó): route `GET /inventory/warehouse-locations/export` gác đúng `inventory.location.view`
     * (Thủ kho có qua wildcard `inventory.*`) và nay trả file .xlsx TRỰC TIẾP trong response
     * (đồng bộ) thay vì tạo async job Owner-only (`excel_export.manage`) như
     * `ExcelActionDropdown` vẫn dùng.
     */
    async exportList(params?: {
      store_id?: string;
      warehouse_id?: string;
      status?: string;
      search?: string;
    }): Promise<void> {
      const response = await api.get<Blob>('/inventory/warehouse-locations/export', {
        params,
        responseType: 'blob',
      });
      const objectUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = 'vi-tri-kho.xlsx';
      link.click();
      URL.revokeObjectURL(objectUrl);
    },
    /**
     * TCR-2082/TCR-2084 — tab "Lịch sử" màn Vị trí kho trước đây gọi thẳng
     * `actionLogService.list()` (route `/action-logs`, gác `action_log.view` — Owner/Manager-only,
     * TCR-1483) nên Thủ kho/Nhân viên kho (chỉ có `inventory.location.*`) không xem được dù họ là
     * người thao tác thật. Route riêng này (`/inventory/warehouse-locations/history`) gác đúng
     * `inventory.location.view`, response cùng shape `{ data, meta }` như action-log cũ.
     */
    history(params?: {
      warehouse_id?: string;
      warehouse_location_id?: string;
      action_type?: string;
      search?: string;
      page?: number;
      limit?: number;
    }): Promise<ActionLogListResponse> {
      return api.get('/inventory/warehouse-locations/history', { params }).then((r) => r.data);
    },
  },

  lots: {
    list(params?: {
      search?: string;
      product_id?: string;
      variant_id?: string;
      store_id?: string;
      warehouse_id?: string;
      warehouse_location_id?: string;
      status?: string;
      page?: number;
      page_size?: number;
    }): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/lots', { params }).then((r) => r.data);
    },
    get(id: string): Promise<ApiResponse<unknown>> {
      return api.get(`/inventory/lots/${id}`).then((r) => r.data);
    },
    availability(params: {
      variant_id: string;
      product_id?: string;
      store_id?: string;
      warehouse_id?: string;
      warehouse_location_id?: string;
      page_size?: number;
    }): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/lots/availability', { params }).then((r) => r.data);
    },
    unassigned(params?: {
      search?: string;
      store_id?: string;
      warehouse_id?: string;
      page?: number;
      page_size?: number;
    }): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/lots/unassigned', { params }).then((r) => r.data);
    },
    locationOccupancy(params?: {
      search?: string;
      store_id?: string;
      warehouse_id?: string;
      warehouse_location_id?: string;
    }): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/lots/location-occupancy', { params }).then((r) => r.data);
    },
    allocateLocation(body: {
      source_balance_id: string;
      warehouse_location_id: string;
      quantity: number;
    }): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/lots/allocate-location', body).then((r) => r.data);
    },
    allocateLocationBulk(body: {
      source_balance_id: string;
      allocations: Array<{ warehouse_location_id: string; quantity: number }>;
    }): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/lots/allocate-location-bulk', body).then((r) => r.data);
    },
    moveLocation(body: {
      source_balance_id: string;
      warehouse_location_id: string;
      quantity: number;
      reason?: string;
    }): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/lots/move-location', body).then((r) => r.data);
    },
    moveLocationBulk(body: {
      source_location_id: string;
      warehouse_location_id: string;
      lines: Array<{ source_balance_id: string; quantity: number }>;
      reason?: string;
    }): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/lots/move-location-bulk', body).then((r) => r.data);
    },
    moveLocationContents(body: {
      source_location_id: string;
      warehouse_location_id: string;
      reason?: string;
    }): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/lots/move-location-contents', body).then((r) => r.data);
    },
    previewAllocation(body: {
      variant_id: string;
      product_id?: string;
      store_id?: string;
      warehouse_id?: string;
      warehouse_location_id?: string;
      quantity: number;
    }): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/lots/allocate-preview', body).then((r) => r.data);
    },
  },

  recalls: {
    list(params?: {
      search?: string;
      status?: string;
      page?: number;
      limit?: number;
    }): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/recalls', { params }).then((r) => r.data);
    },
    create(body: {
      scope_type?: string;
      lot_id?: string;
      lot_number?: string;
      expiry_date?: string;
      variant_id?: string;
      product_id?: string;
      reason?: string;
      note?: string;
    }): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/recalls', body).then((r) => r.data);
    },
    preview(body: {
      lot_id?: string;
      lot_number?: string;
      expiry_date?: string;
      variant_id?: string;
      product_id?: string;
    }): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/recalls/preview', body).then((r) => r.data);
    },
    get(id: string): Promise<ApiResponse<unknown>> {
      return api.get(`/inventory/recalls/${id}`).then((r) => r.data);
    },
    approve(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/recalls/${id}/approve`).then((r) => r.data);
    },
    cancel(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/recalls/${id}/cancel`).then((r) => r.data);
    },
    remove(id: string): Promise<ApiResponse<unknown>> {
      return api.delete(`/inventory/recalls/${id}`).then((r) => r.data);
    },
    updateTask(id: string, taskId: string, body: { status?: string; note?: string; unrecoverable_quantity?: number }): Promise<ApiResponse<unknown>> {
      return api.patch(`/inventory/recalls/${id}/tasks/${taskId}`, body).then((r) => r.data);
    },
    activities(id: string): Promise<ApiResponse<unknown[]>> {
      return api.get(`/inventory/recalls/${id}/activities`).then((r) => r.data);
    },
  },

  printLabels(
    payload: PrintLabelsPayload,
  ): Promise<ApiResponse<{ data: unknown[]; total: number }>> {
    return api.post('/inventory/labels/print', payload).then((r) => r.data);
  },

  // ─── Receipts ──────────────────────────────────────────────────────────
  receipts: {
    list(
      params?: GoodsReceiptListQuery,
      options?: { signal?: AbortSignal },
    ): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/receipts', { params, signal: options?.signal }).then((r) => r.data);
    },
    get(id: string): Promise<ApiResponse<unknown>> {
      return api.get(`/inventory/receipts/${id}`).then((r) => r.data);
    },

    /**
     * Đồng bộ AMIS cho phiếu nhập kho — hai nút, hai mục đích khác nhau:
     *   • `retrySync`  → lỗi HẠ TẦNG (mạng, time-out). Chạy lại với snapshot CŨ.
     *   • `resyncSync` → lỗi NGHIỆP VỤ sau khi kế toán sửa danh mục. Dựng lại snapshot từ dữ
     *     liệu HIỆN TẠI. Đây là nút dùng cho ca hỏng phổ biến nhất của chứng từ kho: mã hàng
     *     hoặc mã kho chưa có trên AMIS. BE trả 400 kèm ĐÚNG lý do nếu vẫn chưa sửa xong.
     */
    retrySync(id: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/receipts/${id}/accounting-sync/retry`)
        .then((r) => r.data);
    },
    resyncSync(id: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/receipts/${id}/accounting-sync/resync`)
        .then((r) => r.data);
    },
    create(
      body: CreateGoodsReceiptPayload,
    ): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/receipts', body).then((r) => r.data);
    },
    receive(
      body: CreateReceiveGoodsReceiptPayload,
    ): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/receipts/receive', body).then((r) => r.data);
    },
    update(
      id: string,
      body: UpdateGoodsReceiptPayload,
    ): Promise<ApiResponse<unknown>> {
      return api.patch(`/inventory/receipts/${id}`, body).then((r) => r.data);
    },
    submit(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/receipts/${id}/submit`).then((r) => r.data);
    },
    approve(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/receipt/${id}/post`, undefined, {
        headers: { 'Idempotency-Key': createUuid() },
      }).then((r) => r.data);
    },
    cancel(id: string, reason?: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/receipts/${id}/cancel`, { reason })
        .then((r) => r.data);
    },
    delete(id: string): Promise<ApiResponse<unknown>> {
      return api.delete(`/inventory/receipts/${id}`).then((r) => r.data);
    },
  },

  // ─── Issues ─────────────────────────────────────────────────────────────
  issues: {
    list(
      params?: GoodsIssueListQuery,
      options?: { signal?: AbortSignal },
    ): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/issues', { params, signal: options?.signal }).then((r) => r.data);
    },
    get(id: string): Promise<ApiResponse<unknown>> {
      return api.get(`/inventory/issues/${id}`).then((r) => r.data);
    },

    /**
     * Đồng bộ AMIS cho phiếu xuất kho — hai nút, hai mục đích khác nhau:
     *   • `retrySync`  → lỗi HẠ TẦNG (mạng, time-out). Chạy lại với snapshot CŨ.
     *   • `resyncSync` → lỗi NGHIỆP VỤ sau khi kế toán sửa danh mục. Dựng lại snapshot từ dữ
     *     liệu HIỆN TẠI. Đây là nút dùng cho ca hỏng phổ biến nhất của chứng từ kho: mã hàng
     *     hoặc mã kho chưa có trên AMIS. BE trả 400 kèm ĐÚNG lý do nếu vẫn chưa sửa xong.
     */
    retrySync(id: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/issues/${id}/accounting-sync/retry`)
        .then((r) => r.data);
    },
    resyncSync(id: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/issues/${id}/accounting-sync/resync`)
        .then((r) => r.data);
    },
    salesInvoices(
      params?: SalesIssueInvoiceQuery,
    ): Promise<PaginatedResponse<SalesIssueInvoiceOption>> {
      return api.get('/inventory/sales-issue-invoices', { params }).then((r) => r.data);
    },
    create(body: CreateGoodsIssuePayload): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/issues', body).then((r) => r.data);
    },
    update(
      id: string,
      body: UpdateGoodsIssuePayload,
    ): Promise<ApiResponse<unknown>> {
      return api.patch(`/inventory/issues/${id}`, body).then((r) => r.data);
    },
    submit(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/issues/${id}/submit`).then((r) => r.data);
    },
    approve(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/issue/${id}/post`, undefined, {
        headers: { 'Idempotency-Key': createUuid() },
      }).then((r) => r.data);
    },
    cancel(id: string, reason?: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/issues/${id}/cancel`, { reason })
        .then((r) => r.data);
    },
    delete(id: string): Promise<ApiResponse<unknown>> {
      return api.delete(`/inventory/issues/${id}`).then((r) => r.data);
    },
  },

  // ─── Transfers ─────────────────────────────────────────────────────────
  transfers: {
    list(
      params?: StockTransferListQuery,
      options?: { signal?: AbortSignal },
    ): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/transfers', { params, signal: options?.signal }).then((r) => r.data);
    },
    get(id: string): Promise<ApiResponse<unknown>> {
      return api.get(`/inventory/transfers/${id}`).then((r) => r.data);
    },

    /**
     * Đồng bộ AMIS cho phiếu chuyển kho — hai nút, hai mục đích khác nhau:
     *   • `retrySync`  → lỗi HẠ TẦNG (mạng, time-out). Chạy lại với snapshot CŨ.
     *   • `resyncSync` → lỗi NGHIỆP VỤ sau khi kế toán sửa danh mục. Dựng lại snapshot từ dữ
     *     liệu HIỆN TẠI. Đây là nút dùng cho ca hỏng phổ biến nhất của chứng từ kho: mã hàng
     *     hoặc mã kho chưa có trên AMIS. BE trả 400 kèm ĐÚNG lý do nếu vẫn chưa sửa xong.
     */
    retrySync(id: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/transfers/${id}/accounting-sync/retry`)
        .then((r) => r.data);
    },
    resyncSync(id: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/transfers/${id}/accounting-sync/resync`)
        .then((r) => r.data);
    },
    findOrCreateFromPurchaseOrder(
      purchaseOrderId: string,
    ): Promise<StockTransferReferenceResponse> {
      return api
        .post<unknown>(
          `/inventory/transfers/from-purchase-order/${purchaseOrderId}`,
        )
        .then((r) => normalizeStockTransferReference(r.data));
    },
    create(
      body: CreateStockTransferPayload,
    ): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/transfers', body).then((r) => r.data);
    },
    update(
      id: string,
      body: UpdateStockTransferPayload,
    ): Promise<ApiResponse<unknown>> {
      return api.patch(`/inventory/transfers/${id}`, body).then((r) => r.data);
    },
    submit(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/transfers/${id}/submit`).then((r) => r.data);
    },
    approve(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/transfers/${id}/approve`).then((r) => r.data);
    },
    post(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/transfers/${id}/post`, undefined, {
        headers: { 'Idempotency-Key': createUuid() },
      }).then((r) => r.data);
    },
    receive(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/transfers/${id}/complete`, undefined, {
        headers: { 'Idempotency-Key': createUuid() },
      }).then((r) => r.data);
    },
    cancel(id: string, reason?: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/transfers/${id}/cancel`, { reason })
        .then((r) => r.data);
    },
    delete(id: string): Promise<ApiResponse<unknown>> {
      return api.delete(`/inventory/transfers/${id}`).then((r) => r.data);
    },
    unpostImpact(id: string): Promise<ApiResponse<{
      document_no?: string;
      movement_count: number;
      quantity_impact: number;
      value_impact: number;
      consumed_layer_count: number;
      can_unpost: boolean;
    }>> {
      return api.get(`/inventory/transfers/${id}/unpost-impact`).then((r) => r.data);
    },
    unpost(id: string, reason: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/transfers/${id}/unpost`, { reason }).then((r) => r.data);
    },
  },

  // ─── Counts / Stock Takes ───────────────────────────────────────────────
  stockTakes: {
    list(
      params?: StockTakeListQuery,
    ): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/stock-takes', { params }).then((r) => r.data);
    },
    get(id: string): Promise<ApiResponse<unknown>> {
      return api.get(`/inventory/stock-takes/${id}`).then((r) => r.data);
    },
    create(body: CreateStockTakePayload): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/stock-takes', body).then((r) => r.data);
    },
    scanLocation(id: string, warehouse_location_id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/stock-takes/${id}/scan-location`, { warehouse_location_id }).then((r) => r.data);
    },
    scanItem(id: string, payload: { warehouse_location_id: string; code: string }): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/stock-takes/${id}/scan-item`, payload).then((r) => r.data);
    },
    count(
      id: string,
      items: StockTakeCountItemPayload[],
      unknownSkus: string[] = [],
    ): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/stock-takes/${id}/count`, { items, unknown_skus: unknownSkus }).then((r) => r.data);
    },
    addItems(id: string, codes: string[]): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/stock-takes/${id}/add-items`, { codes }).then((r) => r.data);
    },
    submit(id: string, options?: { check_snapshot?: boolean; movement_note?: string }): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/stock-takes/${id}/submit`, options ?? {}).then((r) => r.data);
    },
    review(id: string, decisions: Array<{ item_id: string; decision: 'adjust' | 'ignore' | 'recount'; notes?: string }>): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/stock-takes/${id}/review`, { decisions }).then((r) => r.data);
    },
    approve(id: string): Promise<ApiResponse<unknown>> {
      // This action is the stock-count approval that creates the linked
      // adjustment documents. Keep it on the counts.approve route; the
      // costing.manage route is reserved for the separate completion step.
      return api.post(`/inventory/counts/${id}/approve`).then((r) => r.data);
    },
    completeResolution(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/stock-takes/${id}/complete-resolution`).then((r) => r.data);
    },
    reject(id: string, reason?: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/stock-takes/${id}/reject`, { reason })
        .then((r) => r.data);
    },
  },

  workflow: {
    unpostImpact(kind: 'receipt' | 'issue' | 'transfer', id: string): Promise<ApiResponse<{ document_no?: string; movement_count: number; quantity_impact: number; value_impact: number; consumed_layer_count: number; can_unpost: boolean }>> {
      return api.get(`/inventory/${kind}/${id}/unpost-impact`).then((r) => r.data);
    },
    post(kind: 'receipt' | 'issue' | 'transfer', id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/${kind}/${id}/post`, undefined, {
        headers: { 'Idempotency-Key': createUuid() },
      }).then((r) => r.data);
    },
    unpost(kind: 'receipt' | 'issue' | 'transfer', id: string, reason: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/${kind}/${id}/unpost`, { reason }).then((r) => r.data);
    },
  },

  costingRuns: {
    create(body: CreateCostingRunPayload): Promise<ApiResponse<CostingRunCreateData>> {
      return api.post('/inventory/costing-runs', body, { headers: { 'Idempotency-Key': createUuid() } }).then((r) => r.data);
    },
    preview(body: CreateCostingRunPayload): Promise<ApiResponse<CostingRunPreviewResponse>> {
      return api.post('/inventory/costing-runs/preview', body).then((r) => r.data);
    },
    get(id: string): Promise<ApiResponse<CostingRunDetail | null>> {
      return api.get(`/inventory/costing-runs/${id}`).then((r) => r.data);
    },
    logs(id: string, params: CostingRunLogsQuery = {}): Promise<ApiResponse<CostingRunLogPage>> {
      return api.get(`/inventory/costing-runs/${id}/logs`, { params }).then((r) => r.data);
    },
    list(params: { limit?: number; store_id?: string } = {}): Promise<ApiResponse<CostingRun[]>> {
      return api.get('/inventory/costing-runs', { params: { limit: 20, ...params } }).then((r) => r.data);
    },
    reverse(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/costing-runs/${id}/reverse`).then((r) => r.data);
    },
  },

  counts: {
    list(
      params?: StockTakeListQuery,
    ): Promise<ApiResponse<PaginatedResponse<unknown>>> {
      return api.get('/inventory/counts', { params }).then((r) => r.data);
    },
    get(id: string): Promise<ApiResponse<unknown>> {
      return api.get(`/inventory/counts/${id}`).then((r) => r.data);
    },
    create(body: CreateStockTakePayload): Promise<ApiResponse<unknown>> {
      return api.post('/inventory/counts', body).then((r) => r.data);
    },
    count(
      id: string,
      items: StockTakeCountItemPayload[],
    ): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/counts/${id}/count`, { items }).then((r) => r.data);
    },
    submit(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/counts/${id}/submit`).then((r) => r.data);
    },
    review(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/counts/${id}/review`).then((r) => r.data);
    },
    approve(id: string): Promise<ApiResponse<unknown>> {
      return api.post(`/inventory/counts/${id}/approve`).then((r) => r.data);
    },
    reject(id: string, reason?: string): Promise<ApiResponse<unknown>> {
      return api
        .post(`/inventory/counts/${id}/reject`, { reason })
        .then((r) => r.data);
    },
  },

  // ─── Serials ────────────────────────────────────────────────────────────
  serials: {
    list(
      params?: SerialQueryParams,
    ): Promise<PaginatedResponse<StockSerialItem>> {
      return api.get('/inventory/serials', { params }).then((r) => r.data);
    },
  },

  // ─── Warehouses ─────────────────────────────────────────────────────────
  warehouses: {
    list(params?: {
      store_id?: string;
      state?: string;
      search?: string;
      page?: number;
      page_size?: number;
    }): Promise<ApiResponse<WarehouseInfo[]>> {
      return api.get('/inventory/warehouses', { params }).then((r) => r.data);
    },
    get(id: string): Promise<ApiResponse<WarehouseInfo>> {
      return api.get(`/inventory/warehouses/${id}`).then((r) => r.data);
    },
    create(body: {
      code: string;
      name: string;
      name_alias?: string;
      store_id?: string;
      is_main_warehouse?: boolean;
      group_name?: string;
      state?: number;
      unit?: string;
      targets?: string[];
      is_default?: boolean;
      allow_negative?: boolean;
      lot_tracking?: boolean;
      location_tracking?: boolean;
      stock_alert_enabled?: boolean;
    }): Promise<ApiResponse<WarehouseInfo>> {
      return api.post('/inventory/warehouses', body).then((r) => r.data);
    },
    update(
      id: string,
      body: Partial<{
        code: string;
        name: string;
        name_alias: string;
        store_id: string;
        is_main_warehouse: boolean;
        group_name: string;
        state: number;
        unit: string;
        targets: string[];
        allow_negative: boolean;
        lot_tracking: boolean;
        location_tracking: boolean;
        stock_alert_enabled: boolean;
      }>,
    ): Promise<ApiResponse<WarehouseInfo>> {
      return api.patch(`/inventory/warehouses/${id}`, body).then((r) => r.data);
    },
    remove(id: string): Promise<ApiResponse<unknown>> {
      return api.delete(`/inventory/warehouses/${id}`).then((r) => r.data);
    },
  },

  // ─── Legacy / alias ─────────────────────────────────────────────────────
  listWarehouses(
    params?: { store_id?: string },
  ): Promise<ApiResponse<WarehouseInfo[]>> {
    return api.get('/inventory/warehouses', { params }).then((r) => r.data);
  },

  // ─── Stock cost ─────────────────────────────────────────────────────────
  getStockCost(
    variantId: string,
    warehouseId: string,
  ): Promise<ApiResponse<{ unit_cost: number; method: string }>> {
    return api
      .get('/inventory/stock-cost', {
        params: { variant_id: variantId, warehouse_id: warehouseId },
      })
      .then((r) => r.data);
  },
};
